import { all, delay, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { AxiosResponse } from 'axios'
import findKey from 'lodash/findKey'
import find from 'lodash/find'
import queryString from 'query-string'
import { resetTaskCounter } from '../../utils/counters'

import { createApiCall, NG_API_CLIENT } from 'services/api'
import {
  ActionType,
  CategoryTypeIds,
  DashboardColumns,
  IGetTaskClientProducts,
  IMoveTask,
  IPostClientTaskConnectProduct,
  IPostClientTaskSagaNew,
  IPropertyValue,
  ITask,
  MESSAGE_PAGE_SIZE,
  StatusTypeIds,
  TASK_CATEGORY_NG_API_URL,
  TASK_MUTATE_NG_API_URL,
  TASK_REPORT_NG_API_SELECTOR,
  TASK_REPORT_NG_API_URL,
  TASK_STATUS_NG_API_URL,
  TaskStatus,
} from './model'
import {
  Action,
  ActionCallback,
  ActionOptionalCallback,
  DEFAULT_ENTITY_ITEM_LIST,
  DEFAULT_ITEM_LIST,
  IValidationCallbackProps,
} from '../model'
import { history } from '../configureStore'
import { AnyType, IPredicate, withTags } from '@centra/centra-ui'
import { fetchClientProducts } from '../productApi'
import {
  buildAccountManagersTasksSelector,
  buildClientsTasksSelector,
  buildDoneTasksSelector,
  buildSingleTaskSelector,
} from 'utils/ngApi'
import { buildUrgentTasksSelector } from '../../utils/ngApi'
import { IUser } from 'store/authentication/model'
import { TaskManagerTemplateGidByCategoryIdRemote } from 'components/CreateTaskModalRemote'

const buildTaskParticipants = (data: any, user?: IUser) => {
  const isFoundDuplicate = (id: number) => participants.some(participant => participant.id === id)
  const addClientManagerAsParticipant = () => {
    if (!isFoundDuplicate(data.clientManagerId)) {
      participants = [
        ...participants,
        {
          id: data.clientManagerId,
          roles: data.clientManagerRoles,
          firstName: data.clientManagerFirstName,
          lastName: data.clientManagerLastName,
          avatar: data.clientManagerAvatar,
          title: data.clientManagerTitle,
        },
      ]
    }
  }
  const addManagersParticipant = (manager: AnyType) => {
    if (!isFoundDuplicate(manager.id)) {
      participants = [
        ...participants,
        {
          id: manager.id,
          roles: manager.roles,
          firstName: manager.first_name,
          lastName: manager.last_name,
          avatar: manager.avatar,
          title: manager.title,
        },
      ]
    }
  }
  const addAccountManagerAsParticipant = (mandatory = false) => {
    if ((mandatory || data.assignedAccountId === data.clientManagerId) && !isFoundDuplicate(data.accountManagerId)) {
      participants = [
        ...participants,
        {
          id: data.accountManagerId,
          roles: data.accountManagerRoles,
          firstName: data.accountManagerFirstName,
          lastName: data.accountManagerLastName,
          avatar: data.accountManagerAvatar,
          title: data.accountManagerTitle,
        },
      ]
    }
  }
  const addLastCommunicationUserAsParticipant = () => {
    if (!isFoundDuplicate(data.lastCommunicationUser.id)) {
      participants = [
        ...participants,
        {
          id: data.lastCommunicationUser.id,
          roles: data.lastCommunicationUser.roles,
          firstName: data.lastCommunicationUser.firstName,
          lastName: data.lastCommunicationUser.lastName,
          avatar: data.lastCommunicationUser.avatar,
          title: data.lastCommunicationUser.title,
        },
      ]
    }
  }
  const addSupportUsersAsParticipants = () => {
    const supportUsers =
      data.supportUsers?.reduce((acc: any[], user: any) => {
        if (!isFoundDuplicate(user.id)) {
          return [...acc, user]
        }
        return acc
      }, []) || []

    participants = [...participants, ...supportUsers]
  }
  const addTaskAuthorAsParticipant = () => {
    if (data.author && !isFoundDuplicate(data.author.id)) {
      participants = [...participants, data.author]
    }
  }

  let participants = [
    {
      id: data.assignedAccountId,
      roles: data.assignedAccountRoles,
      firstName: data.assignedAccountFirstName,
      lastName: data.assignedAccountLastName,
      avatar: data.assignedAccountAvatar,
      title: data.assignedAccountTitle,
    },
  ]

  addTaskAuthorAsParticipant()

  if (data.categoryId !== CategoryTypeIds.ai) {
    data.clientManagers?.map(addManagersParticipant)
  }

  if (data.assignedAccountId !== data.clientManagerId) {
    addClientManagerAsParticipant()
  }

  // Если категория поддержки
  if (data.categoryId === CategoryTypeIds.support) {
    if (user?.tags.includes('customerUser')) {
      if (data.assignedAccountId === data.accountManagerId) {
        addAccountManagerAsParticipant()
      } else {
        data.lastCommunicationUser?.isSupport
          ? addLastCommunicationUserAsParticipant()
          : addAccountManagerAsParticipant()
      }
    } else {
      addSupportUsersAsParticipants()
      addAccountManagerAsParticipant(true)
    }
  } else if (data.categoryId !== CategoryTypeIds.ai) {
    addAccountManagerAsParticipant(true)
  }

  if (data.lastCommunicationUser?.isFinCoordinator) addLastCommunicationUserAsParticipant()

  return participants
}

const buildPropertyValues = (data: any) =>
  data.propertyValues?.reduce((acc: IPropertyValue[], propertyValue: { [key: string]: unknown }) => {
    let valueField

    switch (propertyValue.type) {
      case 'text':
        valueField = 'textValue'
        break
      case 'integer':
        valueField = 'integerValue'
        break
      case 'decimal':
        valueField = 'decimalValue'
        break
      case 'boolean':
        valueField = 'booleanValue'
        break
      case 'option':
        valueField = 'optionValue'
        break
      default:
        valueField = null
    }

    return valueField
      ? [
          ...acc,
          {
            id: propertyValue.id,
            propertyId: propertyValue.propertyId,
            propertyGid: propertyValue.propertyGid,
            name: propertyValue.name,
            value: propertyValue[valueField],
          },
        ]
      : acc
  }, [])

export const mapNgApiResponse = (response: AxiosResponse | null, task?: any, user?: IUser) => ({
  loading: response?.data.loading,
  totalCount: response?.data.page.totalNumEntries,
  items: response?.data.page.entries.map((item: any) => {
    const remapped = {
      id: item.id,
      name: item.name,
      isUrgent: item.isUrgent,
      unreadMessageCount: item.counterUnreadMessage,
      taskUpdateCount: item.counterTaskUpdate,
      assigned: {
        id: item.assignedAccountId,
        roles: item.assignedAccountRoles,
        firstName: item.assignedAccountFirstName,
        lastName: item.assignedAccountLastName,
        avatar: item.assignedAccountAvatar,
        title: item.assignedAccountTitle,
      },
      category: {
        id: item.categoryId,
        name: item.categoryName,
        colorAlias: item.categoryColor,
      },
      status: {
        id: item.statusId,
        name: item.statusName,
        colorAlias: item.statusColor,
      },
      topic: {
        id: item.topicId,
        name: item.topicName,
      },
      client: {
        id: item.clientId,
        name: item.clientName,
        kind: item.clientKind,
        archivedAt: item.clientArchivedAt,
        agencyName: item.clientAgencyName,
        agencyId: item.clientAgencyId,
        agencyArchivedAt: item.clientAgencyArchivedAt,
      },
      clientManager: {
        id: item.clientManagerId,
        roles: item.clientManagerRoles,
        firstName: item.clientManagerFirstName,
        lastName: item.clientManagerLastName,
        avatar: item.clientManagerAvatar,
        title: item.clientManagerTitle,
      },
      isClientConfirm: item.isClientConfirm,
      createdAt: item.createdAt,
      clientManagerTags: item?.clientManagerTags || [],
      assignedAccountTags: item?.assignedAccountTags || [],
    }
    if (task) {
      return {
        ...remapped,
        active: item.id === task.id,
        participants: buildTaskParticipants(item, user),
        lastCommunicationUser: item.lastCommunicationUser,
        finCoordinators: item.finCoordinators,
        propertyValues: buildPropertyValues(item),
        createdAt: item.createdAt,
        author: item.author,
      }
    }
    return remapped
  }),
})

/**
 * Мои срочные задачи
 * Админский таск-трекер
 */
export function* getUrgentTasksSaga(): Generator {
  yield takeLatest(ActionType.GET_URGENT_TASKS, function* () {
    try {
      const {
        auth: { user },
        taskManager: { task, filter },
      } = yield select()
      let urgentTasks = DEFAULT_ITEM_LIST
      if (withTags({ user, tags: ['role.admin', 'role.supervisor', 'role.account-manager'] })) {
        const selector = buildUrgentTasksSelector(user, filter)
        /**
         * Вместо /task/manager
         */
        const response: AxiosResponse | null = yield createApiCall({
          method: 'POST',
          url: TASK_REPORT_NG_API_URL,
          data: {
            selector,
          },
        })

        if (response?.status === 200) {
          urgentTasks = mapNgApiResponse(response, task)
        }

        yield put({
          type: ActionType.SET_URGENT_TASKS,
          payload: urgentTasks,
        })
      }
    } catch (error) {
      console.error('Inspected error in getUrgentTasksSaga', error)
    }
  })
}

/**
 * Мои задачи
 * Админский таск-трекер
 */
export function* getAccountManagersTasksSaga(): Generator {
  yield takeLatest(ActionType.GET_ACCOUNT_MANAGERS_TASKS, function* () {
    try {
      const {
        auth: { user },
        taskManager: { task, filter },
      } = yield select()

      let accountManagersTasks = DEFAULT_ITEM_LIST

      const selector = buildAccountManagersTasksSelector(user, filter)

      const response: AxiosResponse | null = yield createApiCall({
        method: 'POST',
        url: TASK_REPORT_NG_API_URL,
        data: {
          selector,
        },
      })

      if (response?.status === 200) {
        accountManagersTasks = mapNgApiResponse(response, task)
      }

      yield put({
        type: ActionType.SET_ACCOUNT_MANAGERS_TASKS,
        payload: accountManagersTasks,
      })
    } catch (error) {
      console.error('Inspected error in getAccountManagersTasksSaga', error)
    }
  })
}

/**
 * Задачи клиентов
 * Админский таск-трекер
 */
export function* getClientsTasksSaga(): Generator {
  yield takeLatest(ActionType.GET_CLIENTS_TASKS, function* () {
    try {
      const {
        auth: { user },
        taskManager: { task, filter },
      } = yield select()

      let clientsTasks = DEFAULT_ITEM_LIST

      const selector = buildClientsTasksSelector(user, filter)

      const response: AxiosResponse | null = yield createApiCall({
        apiType: 'task',
        method: 'POST',
        url: TASK_REPORT_NG_API_URL,
        data: {
          selector,
        },
      })

      if (response?.status === 200) {
        clientsTasks = mapNgApiResponse(response, task)
      }

      yield put({
        type: ActionType.SET_CLIENTS_TASKS,
        payload: clientsTasks,
      })
    } catch (error) {
      console.error('Inspected error in getClientsTasksSaga', error)
    }
  })
}

export function* getClientManagersTasksSaga(): Generator {
  yield takeLatest(ActionType.GET_CLIENT_MANAGERS_TASKS, function* () {
    try {
      const {
        auth: { user },
        taskManager: { task },
      } = yield select()

      let response: AxiosResponse | null = null
      let clientManagersTasks = DEFAULT_ITEM_LIST

      if (withTags({ user, tags: ['role.agency-primary-manager', 'role.client-primary-manager'] })) {
        const selector: AnyType = {
          ...TASK_REPORT_NG_API_SELECTOR,
          predicates: [
            ...TASK_REPORT_NG_API_SELECTOR.predicates,
            {
              field: 'clientManagerId',
              operator: 'NOT_IN',
              values: [user.id],
            },
            {
              field: 'statusId',
              operator: 'NOT_IN',
              values: [StatusTypeIds.done, StatusTypeIds.reject],
            },
          ],
        }

        response = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: TASK_REPORT_NG_API_URL,
          data: {
            selector,
          },
        })
      }

      if (response?.status === 200) {
        clientManagersTasks = mapNgApiResponse(response, task)
      }

      yield put({
        type: ActionType.SET_CLIENT_MANAGERS_TASKS,
        payload: clientManagersTasks,
      })
    } catch (error) {
      console.error('Inspected error in getClientManagersTasksSaga', error)
    }
  })
}

/**
 * Выполненные задачи
 * Админский таск-трекер
 */
export function* getDoneTasksSaga(): Generator {
  yield takeLatest(ActionType.GET_DONE_TASKS, function* () {
    try {
      const {
        auth: { user },
        taskManager: { task, filter },
      } = yield select()

      let doneTasks = DEFAULT_ITEM_LIST
      const selector = buildDoneTasksSelector(user, filter)

      const response: AxiosResponse | null = yield createApiCall({
        method: 'POST',
        url: TASK_REPORT_NG_API_URL,
        data: {
          selector,
        },
      })

      if (response?.status === 200) {
        // счетчик с типом finished-task создается только для клиентских ролей и отсутствует у менеджеров
        // его обновление потенциально может вызывать ошибки
        doneTasks = mapNgApiResponse(response, task)
      }

      yield put({
        type: ActionType.SET_DONE_TASKS,
        payload: doneTasks,
      })
    } catch (error) {
      console.error('Inspected error in getDoneTasksSaga', error)
    }
  })
}

export function* getManagersDoneTasksSaga(): Generator {
  yield takeLatest(ActionType.GET_MANAGERS_DONE_TASKS, function* () {
    try {
      const {
        auth: { user },
        taskManager: { task },
      } = yield select()
      let response: AxiosResponse | null = null
      let managersDoneTasks = DEFAULT_ITEM_LIST

      if (withTags({ user, tags: ['role.agency-primary-manager', 'role.client-primary-manager'] })) {
        const selector = {
          ...TASK_REPORT_NG_API_SELECTOR,
          predicates: [
            ...TASK_REPORT_NG_API_SELECTOR.predicates,
            {
              field: 'clientManagerId',
              operator: 'NOT_IN',
              values: [user.id],
            },
            {
              field: 'statusId',
              operator: 'IN',
              values: [StatusTypeIds.done, StatusTypeIds.reject],
            },
          ],
        }

        response = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: TASK_REPORT_NG_API_URL,
          data: {
            selector,
          },
        })

        if (response?.status === 200) {
          managersDoneTasks = mapNgApiResponse(response, task)

          yield put({
            type: ActionType.SET_MANAGERS_DONE_TASKS,
            payload: managersDoneTasks,
          })
        }
      }
    } catch (error) {
      console.error('Inspected error in getManagersDoneTasksSaga', error)
    }
  })
}

/**
 * Вызывается только для админской зоны таск-трекера после создания таска
 */
export function* getAllTasksSaga(): Generator {
  yield takeEvery([ActionType.GET_ALL_TASKS, ActionType.UPDATE_FILTERS_BY_QUERY], function* () {
    try {
      yield all([
        put({ type: ActionType.GET_URGENT_TASKS }),
        put({ type: ActionType.GET_ACCOUNT_MANAGERS_TASKS }),
        put({ type: ActionType.GET_CLIENTS_TASKS }),
        put({ type: ActionType.GET_CLIENT_MANAGERS_TASKS }),
        put({ type: ActionType.GET_DONE_TASKS }),
        put({ type: ActionType.GET_MANAGERS_DONE_TASKS }),
      ])
    } catch (error) {
      console.error('Inspected error in getAllTasksSaga', error)
    }
  })
}

export function* updateFilterSaga(): Generator {
  yield takeEvery(
    [ActionType.ADD_TASK_FILTER, ActionType.REMOVE_TASK_FILTER, ActionType.CLEAR_ALL_TASK_FILTERS],
    function* () {
      try {
        const {
          taskManager: { filter },
        } = yield select()
        const query = queryString.stringify({ ...filter }, { arrayFormat: 'bracket' })
        history.replace(`${history.location.pathname}?${query}`)
      } catch (error) {
        console.error('Inspected error in updateFilterSaga', error)
      }
    }
  )
}

export function* getTaskListByActiveTaskSaga(): Generator {
  yield takeLatest(ActionType.GET_TASK_LIST_BY_ACTIVE_TASK, function* (action: Action<{ id: number }>) {
    try {
      yield delay(1000)
      const { payload } = action
      const {
        taskManager: { tasks },
      } = yield select()
      const taskCategory = findKey(tasks, taskCategotyList => find(taskCategotyList.items, { id: payload.id }))

      switch (taskCategory) {
        case 'urgent':
          yield put({
            type: ActionType.GET_URGENT_TASKS,
          })
          break
        case 'managers':
          yield put({
            type: ActionType.GET_ACCOUNT_MANAGERS_TASKS,
          })
          break
        case 'clientManagers':
          yield put({
            type: ActionType.GET_CLIENT_MANAGERS_TASKS,
          })
          break
        case 'clients':
          yield put({
            type: ActionType.GET_CLIENTS_TASKS,
          })
          break
        case 'done':
          yield put({
            type: ActionType.GET_DONE_TASKS,
          })
          break
        case 'managersDone':
          yield put({
            type: ActionType.GET_MANAGERS_DONE_TASKS,
          })
          break
        default:
      }
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in getTaskListByActiveTaskSaga', error)
    }
  })
}

export function* getCategoriesSaga(): Generator {
  yield takeLatest(ActionType.GET_CATEGORIES, function* () {
    try {
      const { data, status } = yield createApiCall({
        apiType: 'task',
        method: 'POST',
        url: TASK_CATEGORY_NG_API_URL,
        data: {
          selector: {
            fields: ['id', 'name', 'colorAlias', 'active'],
            predicates: [{ field: 'id', operator: 'NOT_EQUALS', values: [CategoryTypeIds.ai] }],
          },
        },
      })

      yield put({
        type: ActionType.SET_CATEGORIES,
        payload: status === 200 ? data.page.entries : [],
      })
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in getCategoriesSaga', error)
    }
  })
}

export function* getStatusesSaga(): Generator {
  yield takeLatest(ActionType.GET_STATUSES, function* () {
    try {
      const { data, status } = yield createApiCall({
        apiType: 'task',
        method: 'POST',
        url: TASK_STATUS_NG_API_URL,
        data: {
          selector: {
            fields: ['id', 'name', 'colorAlias'],
          },
        },
      })

      yield put({
        type: ActionType.SET_STATUSES,
        payload: status === 200 ? data.page.entries : [],
      })
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in getStatusesSaga', error)
    }
  })
}

export function* getTaskClientProductsSaga(): Generator {
  yield takeLatest(ActionType.GET_TASK_CLIENT_PRODUCTS, function* (action: Action<IGetTaskClientProducts>) {
    try {
      const { payload } = action
      let predicates: IPredicate[] = []
      if (payload.clientId) {
        predicates = [{ field: 'clientId', operator: 'EQUALS', values: [payload.clientId] }]
      }
      const { data } = yield fetchClientProducts(predicates, [{ field: 'productConnectedAt', sortOrder: 'DESC' }])

      yield put({
        type: ActionType.SET_TASK_CLIENT_PRODUCTS,
        payload: data.page || DEFAULT_ENTITY_ITEM_LIST,
      })
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in getTaskClientProductsSaga', error)
    }
  })
}

export function* getClientsSaga(): Generator {
  yield takeLatest(ActionType.GET_ACCOUNT_CLIENTS, function* () {
    try {
      const { data, status } = yield createApiCall({
        url: '/clients',
      })

      yield put({
        type: ActionType.SET_ACCOUNT_CLIENTS,
        payload: status === 200 ? data : [],
      })
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in getClientsSaga', error)
    }
  })
}

export function* moveTaskSaga(): Generator {
  yield takeLatest(ActionType.MOVE_TASK, function* (action: Action<IMoveTask>) {
    try {
      const {
        payload: { taskType, task },
      } = action
      const {
        taskManager: { tasks },
        auth: { user },
      } = yield select()

      const newTasks = {
        ...tasks,
      }

      const updateData: any = {}

      if (taskType === DashboardColumns.MY_URGENT_TASKS) {
        updateData.isUrgent = true
        updateData.assignedTo = user.id
      }

      if (taskType === DashboardColumns.MY_TASKS) {
        updateData.assignedTo = user.id
        updateData.isUrgent = false
      }

      if (taskType === DashboardColumns.CLIENT_TASKS) {
        updateData.isUrgent = false
        updateData.assignedTo = task.clientManager.id
      }

      if (taskType === DashboardColumns.FINISHED_TASKS) {
        updateData.isUrgent = false
        updateData.statusId = TaskStatus.DONE
      }

      if (
        taskType !== DashboardColumns.FINISHED_TASKS &&
        (task.status.id === TaskStatus.DONE || task.status.id === TaskStatus.REJECTED)
      ) {
        updateData.statusId = TaskStatus.IN_PROGRESS
      }

      const { data, status } = yield createApiCall({
        apiType: 'task',
        method: 'POST',
        url: TASK_MUTATE_NG_API_URL,
        data: {
          operations: [
            {
              operator: 'SET',
              operand: {
                id: task.id,
                statusId: updateData.statusId || task.status.id,
                isUrgent: updateData.isUrgent,
                assignedTo: updateData.assignedTo || task.assigned.id,
              },
            },
          ],
        },
      })

      if (status === 200) {
        const response: AxiosResponse = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: TASK_REPORT_NG_API_URL,
          data: {
            selector: buildSingleTaskSelector(user, Number(data.results[0].id)),
          },
        })

        newTasks[taskType].items.unshift(mapNgApiResponse(response).items[0])

        if (task.taskType) {
          newTasks[task.taskType] = {
            ...newTasks[task.taskType],
            totalCount: newTasks[task.taskType].totalCount <= 1 ? 0 : newTasks[task.taskType].totalCount - 1,
            // тут выпиливаем перемещаемый таск из старой колонки
            items: newTasks[task.taskType].items.filter(({ id }: ITask) => id !== task.id),
          }
          if (task.taskType !== taskType) {
            newTasks[taskType] = {
              ...newTasks[taskType],
              totalCount: (newTasks[taskType].totalCount || 0) + 1,
              // тут в items уже есть перемещенный таск
              items: [...newTasks[taskType].items],
            }
          }
        }
      }

      yield put({
        type: ActionType.SET_ALL_TASKS,
        payload: newTasks,
      })
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in moveTaskSaga', error)
    }
  })
}

export function* archiveTaskSaga(): Generator {
  yield takeLatest(ActionType.ARCHIVE_TASK, function* (action: ActionCallback<{ id: number }>) {
    try {
      const { payload, callback } = action
      const {
        auth: { user },
      } = yield select()

      const { status } = yield createApiCall({
        apiType: 'task',
        method: 'POST',
        url: TASK_MUTATE_NG_API_URL,
        data: {
          operations: [
            {
              operator: 'SET',
              operand: {
                id: payload.id,
                isArchived: true,
              },
            },
          ],
        },
      })

      if (status === 200) {
        if (withTags({ tags: 'role.account-manager', user })) {
          yield put({
            type: ActionType.GET_ALL_TASKS,
          })
        }
      }
      callback({ success: status === 200 })
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in archiveTaskSaga', error)
    }
  })
}

/**
 * Когда кликаем на карточку таска на канбан доске АМ, Админ
 * Также после создания таск в десктопе в клиентской
 */
export function* changeActiveTaskSaga(): Generator {
  yield takeLatest(ActionType.CHANGE_ACTIVE_TASK, function* (action: ActionOptionalCallback<{ id: number }>) {
    try {
      const { payload, callback } = action

      const {
        auth: { user },
      } = yield select()

      let activeTask: ITask | null = null

      if (payload.id) {
        const response: AxiosResponse = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: TASK_REPORT_NG_API_URL,
          data: {
            selector: buildSingleTaskSelector(user, payload.id),
          },
        })

        activeTask = response?.status === 200 ? mapNgApiResponse(response, payload, user).items[0] : null
      } else {
        yield put({
          type: ActionType.SET_TASK_MESSAGES,
          payload: {
            data: [],
            totalCount: 0,
            newMessages: 0,
          },
        })
      }

      yield put({
        type: ActionType.SET_TASK,
        payload: activeTask,
      })

      if (callback) callback({ success: !!activeTask })

      if (activeTask) {
        yield put({
          type: ActionType.GET_TASK_LIST_BY_ACTIVE_TASK,
          payload: {
            id: activeTask.id,
          },
        })
      }
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in changeActiveTaskSaga', error)
    }
  })
}

export function* postTaskSaga(): Generator {
  yield takeLatest(ActionType.POST_TASK, function* (action: ActionCallback) {
    try {
      const { payload, callback } = action
      const {
        auth: { user },
      } = yield select()

      let response: AxiosResponse | null = null

      if (withTags({ user, tags: 'role.account-manager' })) {
        response = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: TASK_MUTATE_NG_API_URL,
          data: {
            operations: [
              {
                operator: 'ADD',
                operand: {
                  name: payload.name,
                  categoryId: payload.categoryId,
                  statusId: payload.statusId,
                  clientId: payload.clientId,
                  assignedTo: payload.assignedTo,
                  clientManagerId: payload.clientManagerId,
                  debtAmount: payload.debtAmount,
                },
              },
            ],
          },
        })
      } else if (
        withTags({
          user,
          tags: [
            'role.agency-manager',
            'role.agency-primary-manager',
            'role.client-manager',
            'role.client-primary-manager',
          ],
        })
      ) {
        const taskData: IPostClientTaskSagaNew = {
          ...payload,
          clientId: payload.clientId || user.clientId,
        }

        // Если передан gid шаблона, используем его
        // Во всех остальных случаях просто используем дефолтный шаблон для категории
        // Ранее использовалась проверка на категорию (саппорт), но уперлись в ограничения связанные с несколькими шаблонами
        // внутри одной категории
        taskData.templateGid = payload.templateGid || TaskManagerTemplateGidByCategoryIdRemote?.[payload.categoryId]

        if (payload.categoryId === CategoryTypeIds.payment) {
          try {
            taskData.debugInfo = JSON.stringify({
              cli1: payload.clientName || 'x',
              cli2: user.clientName || 'x',
            })
          } catch (e) {
            taskData.debugInfo = 'json error'
          }
        }
        //TODO: заменить на ngApi
        response = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: `/task/create-by-template`,
          data: taskData,
        })
        if (response?.status === 200) {
          const id = response.data.taskId
          yield put({
            type: ActionType.CHANGE_ACTIVE_TASK,
            payload: {
              id,
            },
            callback: ({ success }: IValidationCallbackProps) => {
              if (success) history.push(`/task/${id}`)
            },
          })
        }
      }

      if (response?.status === 200) {
        callback({ success: true })
        if (withTags({ tags: 'role.account-manager', user })) {
          // TODO: тут не оч эффективное место - создаем таск в какой-то конкретной категории,
          //  но перезапрашиваем все колонки. Просто так это не поменять и не стоит знать тут логику,
          //  по которой задачи располагаются в колонках, чтобы запрашивать только по нужной.
          //  Скорее надо загружать изменения или как-то на счетчиках понимать что надо запрашивать
          yield put({
            type: ActionType.GET_ALL_TASKS,
          })
        }
      } else {
        callback({ success: false })
      }
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in postTaskSaga', error)
    }
  })
}

export function* postTaskConnectProductSaga(): Generator {
  yield takeLatest(
    ActionType.POST_TASK_CONNECT_PRODUCT,
    function* (action: ActionCallback<IPostClientTaskConnectProduct>) {
      try {
        const { payload, callback } = action
        const { data, status } = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: `/task/create-by-template`,
          data: {
            ...payload,
            // marketplaceProduct
            templateGid: '68ba9d95-fdb4-4ae7-8903-a9c02893039a',
          },
        })
        if (status === 200) {
          callback({ success: true })
          yield put({
            type: ActionType.CHANGE_ACTIVE_TASK,
            payload: {
              id: data.taskId,
            },
            callback: ({ success }: IValidationCallbackProps) => {
              if (success) history.push(`/task/${data.taskId}`)
            },
          })
        } else {
          callback({ success: false })
        }
      } catch (error) {
        console.error('Inspected error in updateFilterSaga in postTaskConnectProductSaga', error)
      }
    }
  )
}

export function* postTaskConnectProductCabinetSaga(): Generator {
  yield takeLatest(
    ActionType.POST_TASK_CONNECT_PRODUCT_CABINET,
    function* (action: ActionCallback<IPostClientTaskConnectProduct>) {
      try {
        const { payload, callback } = action
        const { data, status } = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: `/task/create-by-template`,
          data: {
            ...payload,
            // marketplaceCabinet
            templateGid: 'ad7faba1-da4e-458b-8a32-0b411b625c13',
          },
        })

        if (status === 200) {
          callback({ success: true })
          yield put({
            type: ActionType.CHANGE_ACTIVE_TASK,
            payload: {
              id: data.taskId,
            },
            callback: ({ success }: IValidationCallbackProps) => {
              if (success) history.push(`/task/${data.taskId}`)
            },
          })
        } else {
          callback({ success: false })
        }
      } catch (error) {
        console.error('Inspected error in updateFilterSaga in postTaskConnectProductCabinetSaga', error)
      }
    }
  )
}

export function* patchTaskSaga(): Generator {
  yield takeLatest(ActionType.PATCH_TASK, function* (action: ActionOptionalCallback) {
    try {
      const { payload, callback } = action
      const {
        auth: { user },
        taskManager: { task },
      } = yield select()

      let response: AxiosResponse | null = null

      const isAccountManager = withTags({ user, tags: 'role.account-manager' })
      if (isAccountManager) {
        response = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: TASK_MUTATE_NG_API_URL,
          data: {
            operations: [
              {
                operator: 'SET',
                operand: {
                  id: payload.id,
                  categoryId: payload.category.id,
                  statusId: payload.status.id,
                  assignedTo: payload.assigned.id,
                  name: payload.name,
                },
              },
            ],
          },
        })
      } else if (
        withTags({
          user,
          tags: [
            'role.agency-manager',
            'role.agency-primary-manager',
            'role.client-manager',
            'role.client-primary-manager',
          ],
        })
      ) {
        response = yield createApiCall({
          apiType: 'task',
          method: 'POST',
          url: TASK_MUTATE_NG_API_URL,
          data: {
            operations: [
              {
                operator: 'SET',
                operand: {
                  id: payload.id,
                  assignedTo: payload.assigned.id,
                },
              },
            ],
          },
        })
        if (response?.status === 200) {
          yield put({
            type: ActionType.SET_TASK,
            payload: {
              ...task,
              assigned: payload.assigned,
            },
          })
        }
      }

      if (isAccountManager) {
        yield put({
          type: ActionType.GET_ALL_TASKS,
        })
      }

      if (callback) callback({ success: response?.status === 200 })
    } catch (error) {
      console.error('Inspected error in updateFilterSaga in patchTaskSaga', error)
    }
  })
}

async function fetchTaskHistoryByIdAndResetCounters(taskId: number, offset: number) {
  const predicates: any = [{ field: 'taskId', operator: 'EQUALS', values: [taskId] }]

  const res = await NG_API_CLIENT.get('reportTaskHistory', {
    selector: {
      fields: ['id', 'kind', 'taskId', 'params', 'sender', 'createdAt', 'createdBy'],
      predicates: predicates,
      ordering: [{ field: 'createdAt', sortOrder: 'DESC' }],
      paging: {
        offset: offset,
        limit: MESSAGE_PAGE_SIZE,
      },
    },
  })

  if (res?.status === 200) {
    // Этот код ранее был в самом апи получения сообщений
    // Т.е. бекенд просто сбрасывал счетчики если фронт просто запросил данные
    // Так как теперь там ng api, то получается, что мы так сделать не можем,
    // впрочем я считаю, что в принципе не очень на GET сделать сайд эффектом изменение чего-то
    // поэтому отдельный вызов тут пока кажется даже более понятным, но и дополнительным
    // по количеству запросов на бекенд
    if (res?.data?.page?.entries.length > 0) {
      resetTaskCounter(taskId)
    }
  }

  return res
}

/**
 * Всегда догружает только последние сообщения
 * Если что-то пришло, то:
 * - если пользователь проскролил вверх и не видит нормально последнее сообщение, то при рендере будет показана черная плашка, что есть новое
 * - если у пользователя открыты последние сообщения, то само доскролится до нового
 */
export function* getTaskMessagesIntervalSaga() {
  yield delay(5000)
  try {
    const {
      taskManager: { taskMassages, task },
    } = yield select()

    if (task) {
      const { data, status } = yield fetchTaskHistoryByIdAndResetCounters(task.id, 0)
      if (status === 200) {
        if (data.page.totalNumEntries > taskMassages.totalCount) {
          const newMessageCount = data.page.totalNumEntries - taskMassages.totalCount
          const messages = data.page.entries.reverse().slice(-newMessageCount)
          yield put({
            type: ActionType.SET_TASK_MESSAGES,
            payload: {
              data: [...taskMassages.data, ...messages],
              totalCount: data.page.totalNumEntries,
              newMessages: taskMassages.newMessages + newMessageCount,
            },
          })
        }
        yield put({
          type: ActionType.GET_TASK_MESSAGES_INTERVAL,
        })
      }
    }
  } catch (error) {
    console.error('Inspected error in updateFilterSaga in getTaskMessagesIntervalSaga', error)
  }
}

/* eslint-disable @typescript-eslint/no-unused-vars */

export default [
  fork(getUrgentTasksSaga),
  fork(getAccountManagersTasksSaga),
  fork(getClientsTasksSaga),
  fork(getClientManagersTasksSaga),
  fork(getDoneTasksSaga),
  fork(getManagersDoneTasksSaga),
  fork(getAllTasksSaga),
  fork(updateFilterSaga),
  fork(getTaskListByActiveTaskSaga),
  fork(getCategoriesSaga),
  fork(getStatusesSaga),
  fork(getTaskClientProductsSaga),
  fork(getClientsSaga),
  fork(postTaskSaga),
  fork(postTaskConnectProductSaga),
  fork(postTaskConnectProductCabinetSaga),
  fork(moveTaskSaga),
  fork(archiveTaskSaga),
  fork(changeActiveTaskSaga),
  fork(patchTaskSaga),
]
